import { useState, useEffect } from "react"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { toast } from "@/hooks/use-toast"
import { sanitizeInput, validateEmail, validatePhone, validateFile, sanitizeHtml } from "@/lib/security"

export interface UserProfile {
  id: string
  user_id: string
  email: string
  first_name?: string
  last_name?: string
  phone?: string
  user_type?: 'guest' | 'host' | 'both'
  avatar_url?: string
  bio?: string
  is_verified?: boolean
  created_at: string
  updated_at: string
  auto_booking_confirmation?: boolean
  notification_email?: string
  notification_sms?: string
  notification_methods?: string[]
  host_approved?: boolean
  host_application_status?: 'none' | 'pending' | 'approved' | 'rejected'
  host_application_date?: string
  additional_verified_emails?: string[]
  is_founding_host?: boolean
  founding_host_registered_at?: string
  // Bank account fields for cooperative members
  bank_account_holder?: string
  bank_iban?: string
  bank_name?: string
}

export interface ProfileUpdateData {
  first_name?: string
  last_name?: string
  phone?: string
  user_type?: 'guest' | 'host' | 'both'
  bio?: string
  auto_booking_confirmation?: boolean
  notification_email?: string
  notification_sms?: string
  notification_methods?: string[]
  host_application_status?: 'none' | 'pending' | 'approved' | 'rejected'
  host_approved?: boolean
  // Bank account fields for cooperative members
  bank_account_holder?: string
  bank_iban?: string
  bank_name?: string
}

export const useProfiles = () => {
  const { user } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchProfile = async (userId?: string) => {
    const targetUserId = userId || user?.id
    if (!targetUserId) return null

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', targetUserId)
        .single()

      if (error) throw error
      setProfile(data as UserProfile)
      return data
    } catch (error) {
      console.error('Error fetching profile:', error)
      return null
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (updates: ProfileUpdateData) => {
    if (!user) {
      toast({
        title: "Anmeldung erforderlich",
        description: "Bitte melden Sie sich an, um Ihr Profil zu bearbeiten.",
        variant: "destructive"
      })
      return { success: false }
    }

    // Enhanced security: Validate and sanitize input data
    const validationErrors: string[] = []
    
    if (updates.phone && !validatePhone(updates.phone)) {
      validationErrors.push("Ungültige Telefonnummer")
    }
    
    // Additional validation for name fields
    if (updates.first_name && (updates.first_name.length < 1 || updates.first_name.length > 50)) {
      validationErrors.push("Vorname muss zwischen 1 und 50 Zeichen lang sein")
    }
    
    if (updates.last_name && (updates.last_name.length < 1 || updates.last_name.length > 50)) {
      validationErrors.push("Nachname muss zwischen 1 und 50 Zeichen lang sein")
    }
    
    if (updates.bio && updates.bio.length > 500) {
      validationErrors.push("Bio darf maximal 500 Zeichen lang sein")
    }
    
    if (validationErrors.length > 0) {
      toast({
        title: "Validierungsfehler",
        description: validationErrors.join(", "),
        variant: "destructive"
      })
      return { success: false }
    }

    // Enhanced sanitization with HTML protection for bio
    const sanitizedUpdates = {
      ...updates,
      first_name: updates.first_name ? sanitizeInput(updates.first_name) : updates.first_name,
      last_name: updates.last_name ? sanitizeInput(updates.last_name) : updates.last_name,
      bio: updates.bio ? sanitizeHtml(updates.bio) : updates.bio,
      phone: updates.phone ? sanitizeInput(updates.phone) : updates.phone
    }

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('profiles')
        .update(sanitizedUpdates)
        .eq('user_id', user.id)
        .select()
        .single()

      if (error) throw error

      setProfile(data as UserProfile)
      toast({
        title: "Profil aktualisiert",
        description: "Ihre Änderungen wurden erfolgreich gespeichert.",
      })

      return { success: true, data }
    } catch (error) {
      console.error('Error updating profile:', error)
      toast({
        title: "Fehler beim Aktualisieren",
        description: "Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      })
      return { success: false }
    } finally {
      setLoading(false)
    }
  }

  const uploadAvatar = async (file: File) => {
    if (!user) return { success: false }

    // Security: Validate file before upload
    const fileValidation = validateFile(file)
    if (!fileValidation.valid) {
      toast({
        title: "Ungültige Datei",
        description: fileValidation.error,
        variant: "destructive"
      })
      return { success: false }
    }

    try {
      setLoading(true)
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}/avatar.${fileExt}`

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true })

      if (uploadError) throw uploadError

      // Get public URL
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName)

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: data.publicUrl })
        .eq('user_id', user.id)

      if (updateError) throw updateError

      toast({
        title: "Avatar aktualisiert",
        description: "Ihr Profilbild wurde erfolgreich geändert.",
      })

      // Refresh profile
      await fetchProfile()

      return { success: true, url: data.publicUrl }
    } catch (error) {
      console.error('Error uploading avatar:', error)
      toast({
        title: "Fehler beim Hochladen",
        description: "Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      })
      return { success: false }
    } finally {
      setLoading(false)
    }
  }

  const getHostStats = async (hostUserId?: string) => {
    const targetUserId = hostUserId || user?.id
    if (!targetUserId) return null

    try {
      // Get listings count
      const { data: listings, error: listingsError } = await supabase
        .from('listings')
        .select('id, average_rating, review_count')
        .eq('user_id', targetUserId)
        .eq('is_active', true)

      if (listingsError) throw listingsError

      // Get total bookings count
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('id, status')
        .in('listing_id', listings?.map(l => l.id) || [])

      if (bookingsError) throw bookingsError

      // Calculate stats
      const totalListings = listings?.length || 0
      const totalBookings = bookings?.length || 0
      const completedBookings = bookings?.filter(b => b.status === 'completed').length || 0
      
      // Calculate average rating across all listings
      const ratingsWithCounts = listings?.filter(l => l.average_rating && l.review_count) || []
      let overallRating = null
      
      if (ratingsWithCounts.length > 0) {
        const totalRatingSum = ratingsWithCounts.reduce((sum, listing) => 
          sum + (listing.average_rating * listing.review_count), 0)
        const totalReviewCount = ratingsWithCounts.reduce((sum, listing) => 
          sum + listing.review_count, 0)
        
        if (totalReviewCount > 0) {
          overallRating = Number((totalRatingSum / totalReviewCount).toFixed(1))
        }
      }

      return {
        totalListings,
        totalBookings,
        completedBookings,
        overallRating,
        totalReviews: ratingsWithCounts.reduce((sum, l) => sum + l.review_count, 0)
      }
    } catch (error) {
      console.error('Error fetching host stats:', error)
      return null
    }
  }

  const getGuestStats = async (guestUserId?: string) => {
    const targetUserId = guestUserId || user?.id
    if (!targetUserId) return null

    try {
      // Get bookings count
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('id, status, total_price')
        .eq('user_id', targetUserId)

      if (bookingsError) throw bookingsError

      // Get reviews count
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('id')
        .eq('user_id', targetUserId)

      if (reviewsError) throw reviewsError

      const totalBookings = bookings?.length || 0
      const completedBookings = bookings?.filter(b => b.status === 'completed').length || 0
      const totalSpent = bookings?.filter(b => b.status === 'completed')
        .reduce((sum, b) => sum + Number(b.total_price), 0) || 0
      const reviewsWritten = reviews?.length || 0

      return {
        totalBookings,
        completedBookings,
        totalSpent,
        reviewsWritten
      }
    } catch (error) {
      console.error('Error fetching guest stats:', error)
      return null
    }
  }

  // Fetch profile on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      fetchProfile();
    } else {
      setProfile(null);
      setLoading(false);
    }
  }, [user?.id]); // Only depend on user.id to prevent loops

  const fetchHostProfile = async (hostUserId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('bank_account_holder, bank_iban, bank_name')
        .eq('user_id', hostUserId)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching host profile:', error)
      return null
    }
  }

  return {
    profile,
    loading,
    fetchProfile,
    updateProfile,
    uploadAvatar,
    getHostStats,
    getGuestStats,
    fetchHostProfile
  }
}